import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "./pages/Home";

const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// Centralized lazy loaders so we can prefetch them on idle / hover
const loaders = {
  About: () => import("./pages/about/About"),
  FAQ: () => import("./pages/about/FAQ"),
  ServiceAreas: () => import("./pages/about/ServiceAreas"),
  Reviews: () => import("./pages/about/Reviews"),
  Career: () => import("./pages/about/Career"),
  TeamMember: () => import("./pages/about/TeamMember"),
  Blog: () => import("./pages/Blog"),
  BlogPost: () => import("./pages/BlogPost"),
  CityKitchen: () => import("./pages/cities/CityKitchenPage"),
  Showroom: () => import("./pages/Showroom"),
  HomeRemodelingServices: () => import("./pages/services/HomeRemodelingServices"),
  Kitchen: () => import("./pages/services/Kitchen"),
  Bathroom: () => import("./pages/services/Bathroom"),
  Basement: () => import("./pages/services/Basement"),
  Deck: () => import("./pages/services/Deck"),
  ScreenRoom: () => import("./pages/services/ScreenRoom"),
  Garage: () => import("./pages/services/Garage"),
  HomeImprovement: () => import("./pages/services/HomeImprovement"),
  Roofing: () => import("./pages/services/Roofing"),
  CustomHomeBuilds: () => import("./pages/projects/CustomHomeBuilds"),
  Lanyon: () => import("./pages/projects/Lanyon"),
  Driftwood: () => import("./pages/projects/Driftwood"),
  Gallery: () => import("./pages/Gallery"),
  Contact: () => import("./pages/Contact"),
  ApplyFinancing: () => import("./pages/ApplyFinancing"),
  Terms: () => import("./pages/Terms"),
  Privacy: () => import("./pages/Privacy"),
  Sitemap: () => import("./pages/Sitemap"),
  GetFreeEstimate: () => import("./pages/lp/GetFreeEstimate"),
  NotFound: () => import("./pages/NotFound"),
};

const About = lazy(loaders.About);
const FAQ = lazy(loaders.FAQ);
const ServiceAreas = lazy(loaders.ServiceAreas);
const Reviews = lazy(loaders.Reviews);
const Career = lazy(loaders.Career);
const TeamMember = lazy(loaders.TeamMember);
const Blog = lazy(loaders.Blog);
const BlogPost = lazy(loaders.BlogPost);
const CityKitchen = lazy(loaders.CityKitchen);
const Showroom = lazy(loaders.Showroom);
const HomeRemodelingServices = lazy(loaders.HomeRemodelingServices);
const Kitchen = lazy(loaders.Kitchen);
const Bathroom = lazy(loaders.Bathroom);
const Basement = lazy(loaders.Basement);
const Deck = lazy(loaders.Deck);
const ScreenRoom = lazy(loaders.ScreenRoom);
const Garage = lazy(loaders.Garage);
const HomeImprovement = lazy(loaders.HomeImprovement);
const Roofing = lazy(loaders.Roofing);
const CustomHomeBuilds = lazy(loaders.CustomHomeBuilds);
const Lanyon = lazy(loaders.Lanyon);
const Driftwood = lazy(loaders.Driftwood);
const Gallery = lazy(loaders.Gallery);
const Contact = lazy(loaders.Contact);
const ApplyFinancing = lazy(loaders.ApplyFinancing);
const Terms = lazy(loaders.Terms);
const Privacy = lazy(loaders.Privacy);
const Sitemap = lazy(loaders.Sitemap);
const GetFreeEstimate = lazy(loaders.GetFreeEstimate);
const NotFound = lazy(loaders.NotFound);

const prefetchByPath: Record<string, Array<() => Promise<unknown>>> = {
  "/home-remodeling-services/": [loaders.HomeRemodelingServices],
  "/home-remodeling-services/kitchen-remodeling/": [loaders.Kitchen],
  "/home-remodeling-services/bathroom-remodeling/": [loaders.Bathroom],
  "/home-remodeling-services/basement-finishing/": [loaders.Basement],
  "/home-remodeling-services/deck-construction/": [loaders.Deck],
  "/home-remodeling-services/screen-room/": [loaders.ScreenRoom],
  "/home-remodeling-services/garage-construction-service/": [loaders.Garage],
  "/home-remodeling-services/home-improvement-services/": [loaders.HomeImprovement],
  "/home-remodeling-services/roofing-services/": [loaders.Roofing],
  "/custom-home-builds/": [loaders.CustomHomeBuilds],
  "/custom-home-builds/project-lanyon-ln/": [loaders.Lanyon],
  "/custom-home-builds/project-driftwood-drive/": [loaders.Driftwood],
  "/about/": [loaders.About],
  "/about/frequently-asked-questions/": [loaders.FAQ],
  "/about/service-areas/": [loaders.ServiceAreas],
  "/about/reviews/": [loaders.Reviews],
  "/about/career/": [loaders.Career],
  "/blog/": [loaders.Blog],
  "/gallery/": [loaders.Gallery],
  "/contact/": [loaders.Contact],
  "/apply-now-for-financing/": [loaders.ApplyFinancing],
};

// Prefetch only likely next routes instead of downloading the whole site on first load.
const RoutePrefetcher = () => {
  useEffect(() => {
    const seen = new Set<string>();
    const load = (path: string) => {
      const normalized = path.endsWith("/") ? path : `${path}/`;
      if (seen.has(normalized)) return;
      seen.add(normalized);
      prefetchByPath[normalized]?.forEach((loader) => loader().catch(() => {}));
    };

    const onRoute = (event: Event) => {
      const path = (event as CustomEvent<string>).detail;
      if (path) load(path);
    };

    window.addEventListener("mdcc:prefetch-route", onRoute);

    const ric: (cb: () => void) => number =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback ||
      ((cb: () => void) => window.setTimeout(cb, 1800));

    const warmLikelyRoutes = () => {
      load("/contact/");
      load("/home-remodeling-services/");
      load("/custom-home-builds/");
    };

    const onLoad = () => ric(warmLikelyRoutes);
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });

    return () => {
      window.removeEventListener("mdcc:prefetch-route", onRoute);
      window.removeEventListener("load", onLoad);
    };
  }, []);
  return null;
};

const queryClient = new QueryClient();

const PageFallback = () => <div className="min-h-[50vh] bg-background" />;

const DeferredToasters = () => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const onLoad = () => setEnabled(true);
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, [setEnabled]);

  if (!enabled) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
  );
};

const Site = ({ children }: { children: ReactNode }) => (
  <SiteLayout>
    <Suspense fallback={<PageFallback />}>{children}</Suspense>
  </SiteLayout>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <DeferredToasters />
      <BrowserRouter>
        <RoutePrefetcher />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
          {/* Ads-only landing — no canonical site chrome */}
          <Route path="/lp/get-free-estimate" element={<GetFreeEstimate />} />
          <Route path="/lp/get-free-estimate/" element={<GetFreeEstimate />} />

          {/* Canonical site */}
          <Route path="/" element={<Site><Home /></Site>} />

          <Route path="/about" element={<Site><About /></Site>} />
          <Route path="/about/" element={<Site><About /></Site>} />
          <Route path="/about/frequently-asked-questions" element={<Site><FAQ /></Site>} />
          <Route path="/about/frequently-asked-questions/" element={<Site><FAQ /></Site>} />
          <Route path="/about/service-areas" element={<Site><ServiceAreas /></Site>} />
          <Route path="/about/service-areas/" element={<Site><ServiceAreas /></Site>} />
          <Route path="/about/reviews" element={<Site><Reviews /></Site>} />
          <Route path="/about/reviews/" element={<Site><Reviews /></Site>} />
          <Route path="/about/career" element={<Site><Career /></Site>} />
          <Route path="/about/career/" element={<Site><Career /></Site>} />
          <Route path="/about/team/:slug" element={<Site><TeamMember /></Site>} />
          <Route path="/about/team/:slug/" element={<Site><TeamMember /></Site>} />
          <Route path="/blog" element={<Site><Blog /></Site>} />
          <Route path="/blog/" element={<Site><Blog /></Site>} />
          <Route path="/blog/:slug" element={<Site><BlogPost /></Site>} />
          <Route path="/blog/:slug/" element={<Site><BlogPost /></Site>} />

          {/* City-level service landing pages */}
          <Route path="/kitchen-remodeling/:slug" element={<Site><CityKitchen /></Site>} />
          <Route path="/kitchen-remodeling/:slug/" element={<Site><CityKitchen /></Site>} />

          <Route path="/md-custom-construction-showroom" element={<Site><Showroom /></Site>} />
          <Route path="/md-custom-construction-showroom/" element={<Site><Showroom /></Site>} />

          <Route path="/home-remodeling-services" element={<Site><HomeRemodelingServices /></Site>} />
          <Route path="/home-remodeling-services/" element={<Site><HomeRemodelingServices /></Site>} />
          <Route path="/home-remodeling-services/kitchen-remodeling" element={<Site><Kitchen /></Site>} />
          <Route path="/home-remodeling-services/kitchen-remodeling/" element={<Site><Kitchen /></Site>} />
          <Route path="/home-remodeling-services/bathroom-remodeling" element={<Site><Bathroom /></Site>} />
          <Route path="/home-remodeling-services/bathroom-remodeling/" element={<Site><Bathroom /></Site>} />
          <Route path="/home-remodeling-services/basement-finishing" element={<Site><Basement /></Site>} />
          <Route path="/home-remodeling-services/basement-finishing/" element={<Site><Basement /></Site>} />
          <Route path="/home-remodeling-services/deck-construction" element={<Site><Deck /></Site>} />
          <Route path="/home-remodeling-services/deck-construction/" element={<Site><Deck /></Site>} />
          <Route path="/home-remodeling-services/screen-room" element={<Site><ScreenRoom /></Site>} />
          <Route path="/home-remodeling-services/screen-room/" element={<Site><ScreenRoom /></Site>} />
          <Route path="/home-remodeling-services/garage-construction-service" element={<Site><Garage /></Site>} />
          <Route path="/home-remodeling-services/garage-construction-service/" element={<Site><Garage /></Site>} />
          <Route path="/home-remodeling-services/home-improvement-services" element={<Site><HomeImprovement /></Site>} />
          <Route path="/home-remodeling-services/home-improvement-services/" element={<Site><HomeImprovement /></Site>} />
          <Route path="/home-remodeling-services/roofing-services" element={<Site><Roofing /></Site>} />
          <Route path="/home-remodeling-services/roofing-services/" element={<Site><Roofing /></Site>} />

          <Route path="/custom-home-builds" element={<Site><CustomHomeBuilds /></Site>} />
          <Route path="/custom-home-builds/" element={<Site><CustomHomeBuilds /></Site>} />
          <Route path="/custom-home-builds/project-lanyon-ln" element={<Site><Lanyon /></Site>} />
          <Route path="/custom-home-builds/project-lanyon-ln/" element={<Site><Lanyon /></Site>} />
          <Route path="/custom-home-builds/project-driftwood-drive" element={<Site><Driftwood /></Site>} />
          <Route path="/custom-home-builds/project-driftwood-drive/" element={<Site><Driftwood /></Site>} />

          <Route path="/gallery" element={<Site><Gallery /></Site>} />
          <Route path="/gallery/" element={<Site><Gallery /></Site>} />
          <Route path="/contact" element={<Site><Contact /></Site>} />
          <Route path="/contact/" element={<Site><Contact /></Site>} />
          <Route path="/apply-now-for-financing" element={<Site><ApplyFinancing /></Site>} />
          <Route path="/apply-now-for-financing/" element={<Site><ApplyFinancing /></Site>} />
          <Route path="/terms-of-use" element={<Site><Terms /></Site>} />
          <Route path="/terms-of-use/" element={<Site><Terms /></Site>} />
          <Route path="/privacy-policy" element={<Site><Privacy /></Site>} />
          <Route path="/privacy-policy/" element={<Site><Privacy /></Site>} />
          <Route path="/sitemap" element={<Site><Sitemap /></Site>} />
          <Route path="/sitemap/" element={<Site><Sitemap /></Site>} />

          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
